import { useState } from 'react';

import { AppIcons, AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Chip, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';

const BUTTON_STRINGS = appStrings.common.button;

type TextValueTypes =
  | string
  | number
  | {
      name?: string;
      club?: string;
      school?: string;
      language?: string;
    };

export const getOptionDisplayLabel = (option: TextValueTypes, isSmallScreen: boolean): string => {
  const truncateAtFiftyCharacters = (str: string): string => {
    const truncateAt = isSmallScreen ? 30 : 50;

    return str.length > truncateAt ? str.substring(0, truncateAt) + '...' : str;
  };

  if (typeof option === 'string' || typeof option === 'number')
    return truncateAtFiftyCharacters(String(option));

  if (option?.name) return truncateAtFiftyCharacters(option.name);
  if (option?.club) return truncateAtFiftyCharacters(option.club);
  if (option?.school) return truncateAtFiftyCharacters(option.school);
  if (option?.language) return truncateAtFiftyCharacters(option.language);

  return '';
};

type ShowMoreConfig = Readonly<{
  minPerCategory?: number;
  categories?: string[];
}>;

type ProfileSectionProps = Readonly<{
  title: string;
  titleSx?: SxProps<Theme>;
  variant:
    | {
        type: 'CHIP';
        chipType: 'pill' | 'tag';
        borderColor: 'light' | 'dark';
        values: TextValueTypes[];
        showMoreConfig?: ShowMoreConfig;
      }
    | {
        type: 'ICON_LIST';
        iconName: AppIcons;
        values: TextValueTypes[];
        showMoreConfig?: ShowMoreConfig;
      }
    | {
        type: 'CHILDREN';
        children: React.ReactNode;
      };
}>;

/**
 * If the showMoreConfig is passed in, we need to get the initial values.
 *
 * By default, we will get two items per category. Once you click Show More, we show the rest of the items.
 */
const getInitialValues = (
  values: TextValueTypes[],
  showMoreConfig?: ShowMoreConfig,
): TextValueTypes[] => {
  if (!showMoreConfig?.categories) {
    return values;
  }

  const categoryCount = showMoreConfig.categories.length;
  const itemsPerCategory = showMoreConfig.minPerCategory ?? 2;
  const totalInitialItems = categoryCount * itemsPerCategory;

  if (values.length <= totalInitialItems) {
    return values;
  }

  const getValuesByCategory = (category: string): TextValueTypes[] => {
    return values.filter((value) => {
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some((val) => val === category);
      }

      return false;
    });
  };

  // Get minimum items from each category
  const initialValues = showMoreConfig.categories.flatMap((category) => {
    const categoryValues = getValuesByCategory(category);

    return categoryValues.slice(0, itemsPerCategory);
  });

  // If we still have space, add more items until we reach totalInitialItems
  if (initialValues.length < totalInitialItems) {
    const remainingCount = totalInitialItems - initialValues.length;
    const usedValues = new Set(initialValues);

    const additionalValues = values
      .filter((value) => !usedValues.has(value))
      .slice(0, remainingCount);

    return [...initialValues, ...additionalValues];
  }

  return initialValues;
};

/**
 * Get the content for the section.
 *
 * Returns children, or renders chips
 */
const getContent = (
  variant: ProfileSectionProps['variant'],
  isSmallScreen: boolean,
  showAll: boolean,
): React.ReactNode => {
  if (variant.type === 'CHILDREN') {
    return variant.children;
  }

  const displayValues = showAll
    ? variant.values
    : getInitialValues(variant.values, variant.showMoreConfig);

  if (variant.type === 'CHIP') {
    return (
      <Box sx={chipGroupSx}>
        {displayValues.map((chipLabel, index) => (
          <Chip
            variant={variant.chipType}
            size="lg"
            label={getOptionDisplayLabel(chipLabel, isSmallScreen)}
            key={`${chipLabel}-${index}`}
            sx={variant.borderColor === 'light' ? lightBorderChipSx : darkBorderChipSx}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={iconColumnLabelSx}>
      {displayValues.map((value) => (
        <Box key={JSON.stringify(value)} sx={iconGroupSx}>
          <Box sx={iconSx}>
            <AppIcon
              iconName={variant.iconName}
              diameter="16px"
              color="Colors/Foreground/fg-primary"
            />
          </Box>
          <Typography
            variant="text-sm-medium"
            color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
          >
            {getOptionDisplayLabel(value, isSmallScreen)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const PublicProfileSection = ({ title, titleSx, variant }: ProfileSectionProps) => {
  const isSmallScreen = useMediaQuery('(max-width: 600px)');
  const [showAll, setShowAll] = useState(false);

  // if the variant is not a children, and there are no values, return null
  if (variant.type !== 'CHILDREN') {
    const flattenedValues = variant.values
      .flatMap((value) => getOptionDisplayLabel(value, isSmallScreen))
      .filter((value): value is string => Boolean(value?.trim()));

    if (flattenedValues.length === 0) {
      return null;
    }
  }

  /**
   * Show a "Show more" button if:
   * 1. The variant is not children
   * 2. showMoreConfig is passed in
   * 3. There are more values than the minimum per category
   */
  const hasMoreToShow =
    variant.type !== 'CHILDREN' &&
    variant.showMoreConfig?.categories &&
    variant.values.length >
      variant.showMoreConfig.categories.length * (variant.showMoreConfig.minPerCategory ?? 2);

  return (
    <Box sx={sectionSx}>
      <Typography
        variant="text-sm-medium"
        color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
        sx={titleSx}
      >
        {title}
      </Typography>
      {getContent(variant, isSmallScreen, showAll)}
      {hasMoreToShow && (
        <Typography
          variant="text-sm-semibold"
          onClick={() => setShowAll((prev) => !prev)}
          sx={viewMoreSx}
        >
          {showAll ? BUTTON_STRINGS.showLess : BUTTON_STRINGS.showMore}
        </Typography>
      )}
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: backrs.spacing['spacing-md'],
});

const chipGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const darkBorderChipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const lightBorderChipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const iconColumnLabelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: backrs.spacing['spacing-sm'],
});

const iconSx: SxProps<Theme> = ({ backrs }) => ({
  width: backrs.spacing['spacing-xl'],
  height: backrs.spacing['spacing-xl'],
  alignSelf: 'flex-start',
});

const iconGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  columnGap: backrs.spacing['spacing-sm'],
});

const viewMoreSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
  cursor: 'pointer',
  marginTop: `${backrs.spacing['spacing-sm']} !important`,
  alignSelf: 'flex-end',
});
